import type { DateRangePreset } from '@reports/domain/types';
import { DateRangePresets } from '@reports/domain/types';
import dayjs from 'dayjs';
import { DateFormats } from '@reports/domain/types';

/**
 * Value object representing a date range for report filtering
 *
 * Encapsulates date range logic with validation and business rules.
 * Immutable by design to ensure data integrity.
 *
 * @example
 * const todayDateRange = createReportDateRange(startDate, endDate, DateRangePresets.TODAY);
 * const customDateRange = createReportDateRange(startDate, endDate, DateRangePresets.DATE_RANGE);
 */
export type ReportDateRange = Readonly<{ start: Date; end: Date; preset: DateRangePreset }>;

/**
 * Factory function to create a new validated ReportDateRange instance
 *
 * @param {Date} start - Start date of the range
 * @param {Date} end - End date of the range
 * @param {DateRangePreset} preset - Preset type for the date range
 * @returns {ReportDateRange} Validated date range value object
 * @throws {Error} If dates are invalid or start is after end
 *
 * @example
 * const dateRange = createReportDateRange(new Date('2025-01-01'), new Date('2025-01-31'), DateRangePresets.THIS_MONTH);
 */
export const createReportDateRange = (start: Date, end: Date, preset: DateRangePreset): ReportDateRange => {
  if (!start || !end || !preset) {
    throw new Error('Start, end dates and preset are required');
  }

  if (start > end) {
    throw new Error('Start date must be before end date');
  }

  return Object.freeze({ start, end, preset });
};

/**
 * Factory function to create a ReportDateRange from a preset
 *
 * Calculates the start and end dates based on the preset type.
 * This encapsulates the business logic for date range calculations.
 *
 * @param {DateRangePreset} preset - Preset type to calculate dates for
 * @param {Date} [referenceDate=new Date()] - Reference date for calculations (defaults to today)
 * @returns {ReportDateRange} Date range value object with calculated dates
 *
 * @example
 * // Get today's date range
 * const todayRange = createReportDateRangeFromPreset(DateRangePresets.TODAY);
 *
 * @example
 * // Get this week's range
 * const weekRange = createReportDateRangeFromPreset(DateRangePresets.THIS_WEEK);
 */
export const createReportDateRangeFromPreset = (
  preset: DateRangePreset,
  referenceDate: Date = new Date(),
): ReportDateRange => {
  const today = new Date(referenceDate);
  today.setHours(0, 0, 0, 0);

  let start: Date;
  let end: Date = new Date(today);

  switch (preset) {
    case DateRangePresets.TODAY: {
      start = new Date(today);
      end = new Date(today);
      break;
    }
    case DateRangePresets.YESTERDAY: {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      start = yesterday;
      end = yesterday;
      break;
    }
    case DateRangePresets.THIS_WEEK: {
      const weekStart = new Date(today);
      const dayOfWeek = weekStart.getDay();
      const diff = weekStart.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Monday as start of week
      weekStart.setDate(diff);
      weekStart.setHours(0, 0, 0, 0);
      start = weekStart;
      end = new Date(today);
      break;
    }
    case DateRangePresets.THIS_MONTH: {
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      start = monthStart;
      end = new Date(today);
      break;
    }
    case DateRangePresets.THIS_YEAR: {
      const yearStart = new Date(today.getFullYear(), 0, 1);
      start = yearStart;
      end = new Date(today);
      break;
    }
    case DateRangePresets.DATE_RANGE: {
      // For custom date range, default to today
      start = new Date(today);
      end = new Date(today);
      break;
    }
    default: {
      start = new Date(today);
      end = new Date(today);
    }
  }

  return createReportDateRange(start, end, preset);
};

/**
 * Formats a date to the standard format used in the application
 *
 * @param {Date} date - Date to format
 * @returns {string} Formatted date string (YYYY-MM-DD)
 *
 * @example
 * const formatted = getDateFormatted(new Date('2025-01-15')); // "2025-01-15"
 */
export const getDateFormatted = (date: Date): string => {
  return dayjs(date).format(DateFormats.YYYY_MM_DD);
};
